import React, { useState, useCallback, useMemo } from "react";

import ModalField, {
	EditButtonRenderProps
} from "components/ModalField/ModalField";
import FieldText from "components/FieldText";
import { useModalField } from "hooks/useModalField";

interface ModalFieldTextProps {
	onCancel?: () => void;
	onOK: (newValue: string) => void;
	title: string;
	helptext?: string;
	value: string;
	isUnlocked?: boolean;
	formatValue?: (value: string) => string;
	editButton?: (props: EditButtonRenderProps) => React.ReactNode;
	subType?: "text" | "tel" | "number";
	validate?: (value: string) => boolean;
	placeholder?: string;
}

const ModalFieldText: React.FunctionComponent<ModalFieldTextProps> = ({
	onCancel = () => undefined,
	onOK,
	title,
	helptext,
	value,
	isUnlocked = true,
	formatValue = value => value,
	editButton,
	subType = "text",
	validate,
	placeholder
}) => {
	const {
		currentValue,
		setCurrentValue,
		isOpen,
		setIsOpen,
		toggleIsOpen
	} = useModalField({ value });

	const [
		shouldHighlightValidationError,
		setShouldHighlightValidationError
	] = useState(false);

	const handleOK = useCallback(() => {
		if (!!validate && !validate(currentValue)) {
			setShouldHighlightValidationError(true);
			return;
		}
		setShouldHighlightValidationError(false);
		onOK(currentValue);
		setIsOpen(false);
	}, [onOK, currentValue, setIsOpen, validate]);

	const handleKeyUp = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			setShouldHighlightValidationError(false);
			if (e.keyCode === 13) {
				handleOK();
			}
		},
		[handleOK]
	);

	const handleCancel = useCallback(() => {
		onCancel();
		setIsOpen(false);
	}, [onCancel, setIsOpen]);

	const displayValue = useMemo(() => formatValue(value), [value, formatValue]);

	return (
		<ModalField
			isUnlocked={isUnlocked}
			toggleIsOpen={toggleIsOpen}
			displayValue={displayValue}
			isOpen={isOpen}
			handleOK={handleOK}
			handleCancel={handleCancel}
			title={title}
			editButton={editButton}
		>
			<FieldText
				subType={subType}
				value={currentValue}
				onChange={e => {
					setCurrentValue(e.currentTarget.value);
				}}
				onKeyUp={handleKeyUp}
				highlightValidationError={shouldHighlightValidationError}
				placeholder={placeholder}
			/>
		</ModalField>
	);
};

export default ModalFieldText;
